/**
 * Resumable-submit intent, persisted in its OWN localStorage key.
 *
 * The wizard's submit is a multi-step client orchestration (create RPC ->
 * uploads -> submit RPC). When any step after creation fails, the order is
 * already a DB draft the podiatrist believes was submitted. This module
 * remembers just enough to offer "Retry submit" - including across a reload.
 *
 * DELIBERATELY SEPARATE from the wizard draft blob ('insolech-new-order-
 * draft-v2'): the PR #55 entry guard drops any draft blob carrying a
 * draftOrderId to stop a previous patient's data from rehydrating into the
 * next order (patient safety). The intent carries NO patient or prescription
 * data - only the order id/number and the OWNING USER - so it can safely
 * survive the guard and a reload.
 *
 * USER SCOPING (adversarial-review finding): localStorage is device-global
 * and outlives logout. Without an owner check, user B on a shared clinic
 * machine would see user A's resume banner (order-number disclosure) and
 * could one-click submit A's half-finished order. Every load therefore
 * requires the CURRENT user's id and drops (removes) the blob on mismatch;
 * AuthContext also clears the key on sign-out. Intents older than MAX_AGE_MS
 * are dropped too - an ancient intent resurfacing weeks later is noise at
 * best and a wrong-order submit at worst.
 */

export const SUBMIT_INTENT_KEY = 'insolech-order-submit-intent-v1';

/** Intents older than this are ignored and removed on load (7 days). */
export const SUBMIT_INTENT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface SubmitIntent {
  /** DB id of the created-but-not-yet-submitted order. */
  orderId: string;
  /** Display number when known (shown in the resume banner). */
  orderNumber: string | null;
  /** auth user id that created the order - the intent's OWNER. */
  userId: string;
  /** Epoch ms when the intent was written. */
  createdAt: number;
}

function isSubmitIntent(v: unknown): v is SubmitIntent {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.orderId === 'string' &&
    o.orderId.length > 0 &&
    (o.orderNumber === null || typeof o.orderNumber === 'string') &&
    typeof o.userId === 'string' &&
    o.userId.length > 0 &&
    typeof o.createdAt === 'number'
  );
}

/**
 * Load the persisted intent FOR the given user. Malformed blobs (including
 * legacy ones without userId), other users' intents and expired intents are
 * removed and yield null - the caller never has to re-check ownership.
 */
export function loadSubmitIntent(expectedUserId: string, nowMs = Date.now()): SubmitIntent | null {
  try {
    const raw = localStorage.getItem(SUBMIT_INTENT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isSubmitIntent(parsed)) {
      clearSubmitIntent();
      return null;
    }
    if (parsed.userId !== expectedUserId) {
      // Another user's leftover on a shared machine: never surface it.
      clearSubmitIntent();
      return null;
    }
    if (nowMs - parsed.createdAt > SUBMIT_INTENT_MAX_AGE_MS) {
      clearSubmitIntent();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveSubmitIntent(intent: SubmitIntent): void {
  try {
    localStorage.setItem(SUBMIT_INTENT_KEY, JSON.stringify(intent));
  } catch {
    /* storage full/blocked: retry simply won't survive a reload */
  }
}

export function clearSubmitIntent(): void {
  try {
    localStorage.removeItem(SUBMIT_INTENT_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Given the staged file names and the file names already attached to the
 * order (order_line_files rows), return the staged names that still need
 * uploading. Pure so the retry-only-missing-uploads rule is unit-testable.
 */
export function missingUploadNames(
  staged: readonly string[],
  alreadyUploaded: readonly string[],
): string[] {
  const uploaded = new Set(alreadyUploaded);
  return staged.filter((name) => !uploaded.has(name));
}
